import { useEffect, useRef, useState } from "react"
import { publicInputClasses } from "./components"

const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"]
const monthFormatter = new Intl.DateTimeFormat("en", { month: "long", year: "numeric" })
const longDateFormatter = new Intl.DateTimeFormat("en", { dateStyle: "long" })
const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

function startOfMonth(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

function dateKey(date: Date) {
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${date.getFullYear()}-${month}-${day}`
}

function displayDate(date: Date | null) {
  if (!date) return ""
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${day}/${month}/${date.getFullYear()}`
}

function calendarDays(month: Date) {
  const firstDay = new Date(month.getFullYear(), month.getMonth(), 1 - month.getDay())
  return Array.from({ length: 42 }, (_, index) => new Date(firstDay.getFullYear(), firstDay.getMonth(), firstDay.getDate() + index))
}

function addDays(date: Date, amount: number) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + amount)
}

function isDateDisabled(date: Date, min: Date | null, max: Date | null) {
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  if (min) {
    const minOnly = new Date(min.getFullYear(), min.getMonth(), min.getDate())
    if (dateOnly < minOnly) return true
  }
  if (max) {
    const maxOnly = new Date(max.getFullYear(), max.getMonth(), max.getDate())
    if (dateOnly > maxOnly) return true
  }
  return false
}

function getYearRange(min: Date | null, max: Date | null) {
  const currentYear = new Date().getFullYear()
  const minYear = min ? min.getFullYear() : currentYear - 100
  const maxYear = max ? max.getFullYear() : currentYear + 10
  const years: number[] = []
  for (let year = maxYear; year >= minYear; year--) {
    years.push(year)
  }
  return years
}

export function DatePickerField({
  label,
  name,
  required = false,
  disabled = false,
  min = null,
  max = null,
}: {
  label: React.ReactNode
  name: string
  required?: boolean
  disabled?: boolean
  min?: Date | null
  max?: Date | null
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const calendarRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [selected, setSelected] = useState<Date | null>(null)
  const [viewMonth, setViewMonth] = useState(() => startOfMonth(new Date()))
  const [open, setOpen] = useState(false)
  const [view, setView] = useState<"days" | "months" | "years">("days")
  const days = calendarDays(viewMonth)
  const years = getYearRange(min, max)

  useEffect(() => {
    inputRef.current?.setCustomValidity(required && !selected ? "Please select a date." : "")
  }, [required, selected])

  useEffect(() => {
    if (!open) return

    function handleOutsidePointerDown(event: PointerEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        if (view !== "days") {
          setView("days")
        } else {
          setOpen(false)
          inputRef.current?.focus()
        }
      }
    }

    document.addEventListener("pointerdown", handleOutsidePointerDown)
    document.addEventListener("keydown", handleKeyDown)
    return () => {
      document.removeEventListener("pointerdown", handleOutsidePointerDown)
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [open, view])

  function openPicker() {
    if (disabled) return
    if (selected) setViewMonth(startOfMonth(selected))
    setView("days")
    setOpen(true)
  }

  function selectDate(date: Date) {
    setSelected(date)
    setViewMonth(startOfMonth(date))
    setOpen(false)
    inputRef.current?.focus()
  }

  function focusDate(date: Date) {
    setViewMonth(startOfMonth(date))
    window.setTimeout(() => {
      calendarRef.current?.querySelector<HTMLButtonElement>(`[data-date="${dateKey(date)}"]`)?.focus()
    })
  }

  function handleInputKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter" || event.key === " " || event.key === "ArrowDown") {
      event.preventDefault()
      openPicker()
    }
  }

  function handleDateKeyDown(date: Date, event: React.KeyboardEvent<HTMLButtonElement>) {
    const movement: Record<string, number> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -7,
      ArrowDown: 7,
    }
    const amount = movement[event.key]

    if (amount) {
      event.preventDefault()
      let nextDate = addDays(date, amount)
      // Skip over disabled dates in the movement direction
      const step = amount > 0 ? 1 : -1
      while (isDateDisabled(nextDate, min, max)) {
        nextDate = addDays(nextDate, step)
        // Safety limit to prevent infinite loops
        if (Math.abs(nextDate.getTime() - date.getTime()) > 365 * 24 * 60 * 60 * 1000) break
      }
      if (!isDateDisabled(nextDate, min, max)) {
        focusDate(nextDate)
      }
      return
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      if (!isDateDisabled(date, min, max)) {
        selectDate(date)
      }
    }
  }

  return (
    <label className="block">
      <span className="mb-1.5 block text-base font-medium text-ink sm:mb-2 sm:text-lg">{label}</span>
      <div ref={containerRef} className="relative">
        <input
          ref={inputRef}
          type="text"
          name={name}
          value={displayDate(selected)}
          placeholder="dd/mm/yyyy"
          readOnly
          required={required}
          disabled={disabled}
          aria-label={typeof label === "string" ? label : name}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls={`${name}-calendar`}
          onClick={openPicker}
          onKeyDown={handleInputKeyDown}
          className={`${publicInputClasses} cursor-pointer pr-11`}
        />
        <button
          type="button"
          tabIndex={-1}
          aria-label="Open date picker"
          disabled={disabled}
          onClick={openPicker}
          className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-ink transition-colors hover:text-forest-700 focus:outline-none focus:ring-0 disabled:opacity-50"
        >
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
            <rect x="3.5" y="5" width="17" height="15.5" rx="2" />
            <path d="M7.5 3.5v4M16.5 3.5v4M3.5 9.5h17" />
          </svg>
        </button>

        {open && (
          <div
            ref={calendarRef}
            id={`${name}-calendar`}
            role="dialog"
            aria-label="Choose date"
            className="absolute left-0 top-full z-30 mt-2 w-[min(20rem,calc(100vw-2.5rem))] rounded-lg border border-gold-500 bg-cream-50 p-3 shadow-lg"
          >
            {view === "days" && (
              <>
                <div className="flex items-center justify-between gap-2">
                  <button
                    type="button"
                    aria-label="Previous month"
                    onClick={() => setViewMonth((month) => new Date(month.getFullYear(), month.getMonth() - 1, 1))}
                    className="grid h-8 w-8 shrink-0 place-items-center rounded text-ink transition-colors hover:bg-cream-100 focus:outline-none focus:ring-0"
                  >
                    <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                      <path d="m10 3-5 5 5 5" />
                    </svg>
                  </button>
                  <div className="flex items-center gap-1">
                    <span className="sr-only">{monthNames[viewMonth.getMonth()]} {viewMonth.getFullYear()}</span>
                    <button
                      type="button"
                      onClick={() => setView("months")}
                      className="rounded px-1.5 py-0.5 text-sm font-semibold text-ink hover:bg-cream-100 focus:outline-none focus:ring-0"
                    >
                      {monthNames[viewMonth.getMonth()]}
                    </button>
                    <button
                      type="button"
                      onClick={() => setView("years")}
                      className="rounded px-1.5 py-0.5 text-sm font-semibold text-ink hover:bg-cream-100 focus:outline-none focus:ring-0"
                    >
                      {viewMonth.getFullYear()}
                    </button>
                  </div>
                  <button
                    type="button"
                    aria-label="Next month"
                    onClick={() => setViewMonth((month) => new Date(month.getFullYear(), month.getMonth() + 1, 1))}
                    className="grid h-8 w-8 shrink-0 place-items-center rounded text-ink transition-colors hover:bg-cream-100 focus:outline-none focus:ring-0"
                  >
                    <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                      <path d="m6 3 5 5-5 5" />
                    </svg>
                  </button>
                </div>
                <div className="mt-3 grid grid-cols-7 text-center text-[11px] font-medium text-ink/50" aria-hidden="true">
                  {weekdays.map((weekday) => <span key={weekday}>{weekday}</span>)}
                </div>
                <div role="grid" aria-label={monthFormatter.format(viewMonth)} className="mt-1 grid grid-cols-7 gap-1">
                  {days.map((date) => {
                    const currentMonth = date.getMonth() === viewMonth.getMonth()
                    const isSelected = selected ? dateKey(date) === dateKey(selected) : false
                    const isToday = dateKey(date) === dateKey(new Date())
                    const isDisabled = isDateDisabled(date, min, max)
                    return (
                      <button
                        key={dateKey(date)}
                        type="button"
                        role="gridcell"
                        data-date={dateKey(date)}
                        aria-label={longDateFormatter.format(date)}
                        aria-selected={isSelected}
                        aria-disabled={isDisabled}
                        disabled={isDisabled}
                        onClick={() => selectDate(date)}
                        onKeyDown={(event) => handleDateKeyDown(date, event)}
                        className={`grid h-8 place-items-center rounded text-xs transition-colors focus:outline-none focus:ring-0 ${
                          isDisabled
                            ? "cursor-not-allowed text-ink/20"
                            : isSelected
                              ? "bg-forest-950 font-semibold text-white"
                              : isToday
                                ? "font-semibold text-forest-700 hover:bg-cream-100"
                                : currentMonth
                                  ? "text-ink hover:bg-cream-100"
                                  : "text-ink/30 hover:bg-cream-100"
                        }`}
                      >
                        {date.getDate()}
                      </button>
                    )
                  })}
                </div>
              </>
            )}

            {view === "months" && (
              <>
                <div className="flex items-center justify-between gap-2">
                  <button
                    type="button"
                    aria-label="Previous year"
                    onClick={() => setViewMonth((month) => new Date(month.getFullYear() - 1, month.getMonth(), 1))}
                    className="grid h-8 w-8 shrink-0 place-items-center rounded text-ink transition-colors hover:bg-cream-100 focus:outline-none focus:ring-0"
                  >
                    <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                      <path d="m10 3-5 5 5 5" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => setView("years")}
                    className="rounded px-2 py-0.5 text-sm font-semibold text-ink hover:bg-cream-100 focus:outline-none focus:ring-0"
                  >
                    {viewMonth.getFullYear()}
                  </button>
                  <button
                    type="button"
                    aria-label="Next year"
                    onClick={() => setViewMonth((month) => new Date(month.getFullYear() + 1, month.getMonth(), 1))}
                    className="grid h-8 w-8 shrink-0 place-items-center rounded text-ink transition-colors hover:bg-cream-100 focus:outline-none focus:ring-0"
                  >
                    <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                      <path d="m6 3 5 5-5 5" />
                    </svg>
                  </button>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2">
                  {monthNames.map((month, index) => (
                    <button
                      key={month}
                      type="button"
                      onClick={() => { setViewMonth(new Date(viewMonth.getFullYear(), index, 1)); setView("days") }}
                      className={`rounded px-2 py-2 text-sm transition-colors hover:bg-cream-100 focus:outline-none focus:ring-0 ${
                        index === viewMonth.getMonth() ? "font-semibold text-forest-700" : "text-ink"
                      }`}
                    >
                      {month.slice(0, 3)}
                    </button>
                  ))}
                </div>
              </>
            )}

            {view === "years" && (
              <>
                <div className="flex items-center justify-between gap-2">
                  <button
                    type="button"
                    aria-label="Previous years"
                    onClick={() => setViewMonth((month) => new Date(month.getFullYear() - 12, month.getMonth(), 1))}
                    className="grid h-8 w-8 shrink-0 place-items-center rounded text-ink transition-colors hover:bg-cream-100 focus:outline-none focus:ring-0"
                  >
                    <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                      <path d="m10 3-5 5 5 5" />
                    </svg>
                  </button>
                  <span className="text-sm font-semibold text-ink">Select year</span>
                  <button
                    type="button"
                    aria-label="Next years"
                    onClick={() => setViewMonth((month) => new Date(month.getFullYear() + 12, month.getMonth(), 1))}
                    className="grid h-8 w-8 shrink-0 place-items-center rounded text-ink transition-colors hover:bg-cream-100 focus:outline-none focus:ring-0"
                  >
                    <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                      <path d="m6 3 5 5-5 5" />
                    </svg>
                  </button>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2">
                  {(() => {
                    const startYear = Math.floor(viewMonth.getFullYear() / 12) * 12
                    return Array.from({ length: 12 }, (_, i) => startYear + i)
                      .filter((year) => years.includes(year))
                      .map((year) => (
                        <button
                          key={year}
                          type="button"
                          onClick={() => { setViewMonth(new Date(year, viewMonth.getMonth(), 1)); setView("months") }}
                          className={`rounded px-2 py-2 text-sm transition-colors hover:bg-cream-100 focus:outline-none focus:ring-0 ${
                            year === viewMonth.getFullYear() ? "font-semibold text-forest-700" : "text-ink"
                          }`}
                        >
                          {year}
                        </button>
                      ))
                  })()}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </label>
  )
}
